import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [favorites, setFavorites] = useState(new Set());

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites/ids");
      setFavorites(new Set(data));
    } catch {
      setFavorites(new Set());
    }
  }, []);

  /* eslint-disable */
  const checkSession = useCallback(async () => {
    // SEO win: skip /auth/me + /favorites for anonymous visitors so search
    // engine crawlers don't waste budget on guaranteed-401 endpoints.
    const hasHint = typeof document !== "undefined" && document.cookie.includes("dg_auth_hint=1");
    if (!hasHint) {
      setUser(false);
      setFavorites(new Set());
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshFavorites();
    } catch {
      setUser(false);
      setFavorites(new Set());
    }
  }, [refreshFavorites]);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    setUser(null);
    setFavorites(new Set());
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await refreshFavorites();
    return data;
  };

  const register = async (email, password, name) => {
    setUser(null);
    setFavorites(new Set());
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    await refreshFavorites();
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setFavorites(new Set());
  };

  const toggleFavorite = async (offerId) => {
    if (!user) throw new Error("auth_required");
    const has = favorites.has(offerId);
    const next = new Set(favorites);
    if (has) {
      next.delete(offerId);
      setFavorites(next);
      await api.delete(`/favorites/${offerId}`);
    } else {
      next.add(offerId);
      setFavorites(next);
      await api.post(`/favorites/${offerId}`);
    }
  };
  /* eslint-enable */

  return (
    <AuthContext.Provider value={{ user, login, register, logout, favorites, toggleFavorite, refreshFavorites, refreshSession: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
